import {useInboxBacklogSummaryQuery} from '@transactions/graphql';
import * as React from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {ActivityPage} from 'src/ActivityPage';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {cn} from 'src/lib/utils';
import {ReviewList} from 'src/ReviewPage';
import {TransactionListView} from 'src/TransactionListView';

/**
 * The two layers of one model: what the user recorded (transactions) and
 * what the accounts saw (raw transactions — "activity" everywhere a person
 * reads it).
 */
export type LedgerLayer = 'transactions' | 'activity';

const LAYERS: Array<{key: LedgerLayer; label: string}> = [
  {key: 'transactions', label: 'Transactions'},
  {key: 'activity', label: 'Activity'},
];

/**
 * Filters that mean the same thing on both layers, so they survive a switch.
 * Everything else names something only one layer has (categories and tags;
 * accounts and allocation) and is dropped.
 */
const SHARED_FILTER_KEYS = ['search', 'startDate', 'endDate', 'minAmount', 'maxAmount'];

export const ledgerLayerOf = (params: URLSearchParams): LedgerLayer =>
  params.get('layer') === 'activity' ? 'activity' : 'transactions';

/**
 * The Activity layer's views: every line, or the review queue — the lines
 * no transaction accounts for yet, recent first.
 */
export type ActivityView = 'all' | 'review';

export const ledgerViewOf = (params: URLSearchParams): ActivityView =>
  ledgerLayerOf(params) === 'activity' && params.get('view') === 'review'
    ? 'review'
    : 'all';

/**
 * The URL for an Activity view. Views are different lists with their own
 * filters (review's dates are `start`/`end`, which the Backlog's year rows
 * link with), so a switch starts the view fresh.
 */
export const ledgerViewHref = (view: ActivityView): string =>
  view === 'review' ? '/ledger?layer=activity&view=review' : '/ledger?layer=activity';

/**
 * The URL for switching to a layer, keeping the filters both layers share.
 * It always names the layer: a bare /ledger (the sidebar's link) restores
 * the Transactions list's saved filters, as /transactions did, but a
 * switch must carry exactly the shared filters — never bring back saved
 * categories or tags from an earlier visit.
 */
export const ledgerLayerHref = (current: URLSearchParams, layer: LedgerLayer): string => {
  const next = new URLSearchParams({layer});
  for (const key of SHARED_FILTER_KEYS) {
    const value = current.get(key);
    if (value != null) next.set(key, value);
  }
  return `/ledger?${next.toString()}`;
};

/**
 * One list page for both layers of the ledger, with a toggle between them.
 * Each layer is the existing list — the Transactions list and the Activity
 * browser — hosted unchanged, filters and all; the toggle carries the
 * filters they share (see docs/plans/archive/2026-09-27-unified-ledger.md).
 */
export const LedgerView = () => {
  useBreadcrumb({label: 'Ledger', reset: true});
  const [searchParams] = useSearchParams();
  const layer = ledgerLayerOf(searchParams);
  const view = ledgerViewOf(searchParams);
  const {data: summaryData} = useInboxBacklogSummaryQuery({skip: layer !== 'activity'});
  const reviewCount = summaryData?.inboxBacklogSummary.recentCount ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="border-border flex shrink-0 flex-wrap items-end justify-between gap-x-4 border-b px-4 pt-3">
      <nav aria-label="Ledger layer" className="flex gap-5">
        {LAYERS.map((option) => {
          const current = option.key === layer;
          return (
            <Link
              key={option.key}
              // The current layer links to itself unchanged — a no-op, where
              // the switch URL would drop its layer-only filters.
              to={current ? `?${searchParams.toString()}` : ledgerLayerHref(searchParams, option.key)}
              aria-current={current ? 'page' : undefined}
              className={cn(
                'border-b-2 pb-2 text-sm font-medium no-underline transition',
                current
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {option.label}
            </Link>
          );
        })}
      </nav>
      {layer === 'activity' && (
        <nav aria-label="Activity view" className="mb-2 flex gap-1">
          {(
            [
              {key: 'all', label: 'All'},
              {key: 'review', label: 'Needs review'},
            ] as const
          ).map((option) => {
            const current = option.key === view;
            return (
              <Link
                key={option.key}
                to={current ? `?${searchParams.toString()}` : ledgerViewHref(option.key)}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs no-underline transition',
                  current
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {option.label}
                {option.key === 'review' && reviewCount != null && reviewCount > 0 && (
                  <span className="tabular-nums opacity-80">{reviewCount}</span>
                )}
              </Link>
            );
          })}
        </nav>
      )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {layer === 'transactions' ? (
          <TransactionListView />
        ) : view === 'review' ? (
          <ReviewList variant="full" />
        ) : (
          <ActivityPage />
        )}
      </div>
    </div>
  );
};
